import React, { useState } from 'react';
import { Text, ToastQueue } from '@adobe/react-spectrum';
import classNames from 'classnames';
import { QuillEditor } from 'app/components/RsdForms/CreateMoment/components/QuillEditor';
import { isEmptyHtml } from 'app/components/SlideOutPanel/CommentPanel/utils';
import chevronDropdownSvg from 'app/assets/icons/chevron-dropdown.svg';
import formFieldStyles from 'app/components/RsdForms/CreateMoment/FormField.module.scss';
import { NameListHoverPopover } from './NameListHoverPopover';
import { EmailRecipient } from '../types';
import styles from './FeedbackSlideOutBody.module.scss';

interface NewFeedbackEditorProps {
  emailRecipients?: EmailRecipient[];
  stacked?: boolean;
  /**
   * POSTs the typed content (see FeedbackSlideOut.tsx's `handleSendFeedback`,
   * which appends the response to `thread.entries`) - throws on failure,
   * the same convention `patchFeedbackThreadEntry`/`updateMoment` already use
   * for this app's other rich-text saves, so this component's own try/catch
   * decides the loading/error UI.
   */
  onSend: (content: string) => Promise<void>;
}

/**
 * "NEW FEEDBACK" editor box (Figma node 18686:149466 and children, file
 * hBnevR1MeKGrm062iaGE1P) - reuses the existing `QuillEditor` component the
 * RSD slide-out's own Overview section already uses for its rich-text
 * fields, rather than a new editor. Figma's toolbar and editor area render
 * as two separate rows (a flattened screenshot of the toolbar in the
 * design file), but a real Quill editor already renders its toolbar
 * directly above its own text area as one unit, so this only needs a
 * single label + editor row, not a second hidden-label spacer row.
 *
 * `formFieldStyles.quillWrapper`/`.fullEditor` are the exact classes
 * Overview's own `RichTextEditorComponent` applies around every QuillEditor
 * it renders (white toolbar/background, border, corner radius, fixed
 * editing-area height) - reused verbatim here (not
 * `RichTextEditorComponent` itself, which needs a full `StoreFormField`
 * object and always shows Overview's own character-count footer, neither
 * of which applies to this compact box) so the editor reads identically to
 * Overview's without a second hand-rolled style. No `toolbarOptions`
 * override is passed either, for the same reason
 * FeedbackThreadEntryCard.tsx's own edit-mode QuillEditor doesn't pass
 * one - both fall back to QuillEditor's own single hardcoded default
 * toolbar (QuillEditor.tsx), so New Feedback/Requestor Note/Feedback all
 * render the exact same toolbar, in the same order, with the same
 * behavior - not three separately-configured editors.
 *
 * Hovering "Feedback email recipients" shows the recipient names via
 * NameListHoverPopover.tsx - the exact same hover popover the Approver(s)
 * column's multi-approver case uses (see ApproverCell.tsx), not a second
 * implementation. An empty/missing `emailRecipients` list is handled by
 * that same component (no popover, no listeners - just the plain
 * label+chevron). Send reuses `.sendButton`, the same style Overview's own
 * Save button uses (see FeedbackSlideOutBody.module.scss's comment on that
 * class).
 *
 * Send POSTs via `onSend` (see this file's own prop doc) - disabled while
 * empty or while a send is already in flight (`isSending`, guarding against
 * a duplicate submit from a fast double-click/double-Enter; the button's own
 * label stays "Send" throughout, per Figma - only its disabled state
 * changes). The draft only clears once `onSend` has actually resolved
 * (never optimistically before the request settles), and a
 * `ToastQueue.positive` confirms success at that point; on failure the
 * draft is left exactly as typed (no revert-to-empty) and a
 * `ToastQueue.negative` error shows instead - the same success/failure
 * toast convention (Spectrum's `ToastQueue`, `{ timeout: 5000 }`)
 * Overview.tsx's own rich-text saves already use for the negative case.
 *
 * `quillResetKey` exists solely to actually clear the editor on success:
 * QuillEditor.tsx seeds its own internal `editorHtml` state from `value`
 * only once, on mount (`useState(value)`), and never re-syncs it from a
 * later `value` prop change - it is a controlled-on-mount-only component,
 * not a fully controlled one. Setting `draft` back to `''` alone therefore
 * changes the *prop* but leaves Quill's own DOM content untouched. Bumping
 * `quillResetKey` (used as this element's `key`) forces React to unmount
 * and remount a fresh QuillEditor instance instead, whose internal state
 * then initializes from the now-empty `draft` - the same
 * remount-to-reset trick FeedbackThreadEntryCard.tsx gets "for free" there
 * (its own QuillEditor is already conditionally rendered only while
 * `isEditing` is true, so toggling that off/on already unmounts/remounts
 * it); this editor is always mounted, so it needs an explicit key bump
 * instead.
 *
 * The light-blue tint (`.newFeedbackBox`) wraps only the label+editor row -
 * the footer row (recipients/Send) is a plain sibling below it with its
 * own transparent background, matching Figma's own two separate frames
 * (18686:149466 for the tinted editor, 18686:149473 for the footer) rather
 * than one shared tinted box.
 */
export function NewFeedbackEditor({
  emailRecipients = [],
  stacked = false,
  onSend
}: NewFeedbackEditorProps) {
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [quillResetKey, setQuillResetKey] = useState(0);
  const canSend = !isEmptyHtml(draft) && !isSending;
  const recipientNames = emailRecipients
    .map(recipient => recipient.name)
    .join(', ');

  async function handleSend() {
    if (!canSend) return;
    setIsSending(true);
    try {
      await onSend(draft);
      // Clear only after `onSend` resolves - a rejected promise skips these
      // two lines entirely, leaving the typed draft in place for the catch
      // branch below to preserve. See this file's own doc comment on why
      // `quillResetKey` (not just `setDraft('')`) is required to actually
      // clear QuillEditor's own internal content.
      setDraft('');
      setQuillResetKey(prev => prev + 1);
      ToastQueue.positive('Feedback sent successfully.', { timeout: 5000 });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to send feedback. Please try again.';
      ToastQueue.negative(message, { timeout: 5000 });
    } finally {
      setIsSending(false);
    }
  }

  return (
    <div className={styles.newFeedbackSection}>
      <div
        className={classNames(styles.newFeedbackBox, {
          [styles.newFeedbackBoxStacked]: stacked
        })}
      >
        <Text
          UNSAFE_className={classNames(styles.columnLabel, {
            [styles.stackedLabel]: stacked
          })}
        >
          NEW FEEDBACK
        </Text>
        <div className={styles.newFeedbackEditorColumn}>
          <div
            className={formFieldStyles.quillWrapper}
            style={{ display: 'flex' }}
          >
            <QuillEditor
              key={quillResetKey}
              value={draft}
              onChange={setDraft}
              placeholder="Add feedback…"
              minHeight="100px"
              showCharCount={false}
              className={formFieldStyles.fullEditor}
            />
          </div>
        </div>
      </div>
      <div
        className={classNames(styles.newFeedbackFooter, {
          [styles.newFeedbackFooterStacked]: stacked
        })}
      >
        <NameListHoverPopover
          people={emailRecipients}
          names={recipientNames}
          triggerClassName={styles.recipientsTrigger}
          // This trigger lives inside the Feedback slide-out's own
          // `.slideOutPanel` (SlideOutPanel.module.scss, z-index 100000) -
          // NameListHoverPopover's default z-index (10000, correct for
          // ApproverCell's trigger in normal table content) would render
          // the popover *behind* that panel's own opaque background,
          // technically open but invisible. 200001 matches the z-index
          // tier this same slide-out family already uses for "must render
          // above the sticky header" popovers (see SlideOutPanel.module.scss
          // line ~774's own comment on that exact value).
          zIndex={200001}
          // No native browser title tooltip here - once this popover
          // exists, a plain OS tooltip alongside it just reads as a
          // confusing second, plainer popover.
          showNativeTitle={false}
        >
          <span>Feedback email recipients</span>
          <img src={chevronDropdownSvg} alt="" width={8} height={8} />
        </NameListHoverPopover>
        <button
          type="button"
          className={styles.sendButton}
          disabled={!canSend}
          onClick={handleSend}
        >
          Send
        </button>
      </div>
    </div>
  );
}

export default NewFeedbackEditor;
